import { Flex, Image } from '@chakra-ui/core'
import { useAnimation, Variants } from 'framer-motion'
import * as React from 'react'
import { useMediaQuery } from 'react-responsive'
import { ColorProps } from 'styled-system'
import { useAppContext } from '../../context/AppProvider'
import { NavItem } from '../../navigation'
import { images, theme } from '../../theme'
import Previewer from './Previewer'
import SideBarButton from './SideBarButton'
import SideBarItem from './SideBarItem'
import { MenuCont, MenuCont2, Overlay, RenderWrapper } from './styles'

const PRIMARY_DRAWER_WIDTH = 250

type SideBarProps = ColorProps & {
  accentColor: string
  borderColor?: string
  closeOnNavigate?: boolean
  color: string
  hoverColor: string
  navItems: NavItem[]
  renderUserInfo?: (history: any) => React.ReactNode
}

const SideBar: React.FC<SideBarProps> = ({
  accentColor,
  bg,
  children,
  color,
  hoverColor,
  navItems
}) => {
  const { drawerOpen, toggleDrawer, drawerOpen2, toggleDrawer2 } = useAppContext()
  const controls = useAnimation()
  const controls2 = useAnimation()
  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 40em)' })
  const isTablet = useMediaQuery({ query: '(max-width: 65em)' })

  const SECONDARY_DRAWER_WIDTH = isTablet ? 450 : 700

  React.useEffect(() => {
    if (drawerOpen) {
      controls.start('open')
    } else {
      controls.start('closed')
    }
  }, [isTabletOrMobile, drawerOpen, controls])

  const variants: Variants = {
    open: {
      x: 0,
      width: PRIMARY_DRAWER_WIDTH,
      transition: { staggerChildren: 0.2, delayChildren: 0.1, stiffness: 80 }
    },
    closed: {
      x: isTabletOrMobile ? -PRIMARY_DRAWER_WIDTH : 0,
      width: isTabletOrMobile ? PRIMARY_DRAWER_WIDTH : 64,
      transition: {
        stiffness: 80,
        staggerDirection: -1,
        staggerChildren: 0.2,
        delay: isTabletOrMobile ? 0.25 : 0
      }
    }
  }
  const variants2: Variants = {
    open: {
      x: 0,
      width: SECONDARY_DRAWER_WIDTH,
      transition: { staggerChildren: 0.2, delayChildren: 0.1, stiffness: 80 }
    },
    closed: {
      x: isTabletOrMobile ? SECONDARY_DRAWER_WIDTH : 0,
      width: isTabletOrMobile ? SECONDARY_DRAWER_WIDTH : 0,
      transition: {
        stiffness: 80,
        staggerDirection: -1,
        staggerChildren: 0.2,
        delay: isTabletOrMobile ? 0.25 : 0
      }
    }
  }

  return (
    <>
      <MenuCont
        bg={bg}
        flexDir="column"
        animate={controls}
        variants={variants}
        alignItems="flex-start"
        justifyContent="flex-start"
        initial={{ width: drawerOpen ? PRIMARY_DRAWER_WIDTH : 64 }}
      >
        <Flex pl="20px" width="100%" height="64px" alignItems="center">
          <SideBarButton color={theme.colors.text[50]} open={drawerOpen} onClick={toggleDrawer} />
          {drawerOpen && (
            <Image
              src={images.logo}
              alt="Logo"
              w={150}
              objectFit="contain"
              fallbackSrc="https://via.placeholder.com/50"
            />
          )}
        </Flex>

        {navItems.map((props) => {
          return (
            <SideBarItem
              color={color}
              key={props.title}
              hoverColor={hoverColor}
              accentColor={accentColor}
              {...props}
            />
          )
        })}
      </MenuCont>
      <MenuCont2
        bg={bg}
        flexDir="column"
        animate={controls2}
        variants={variants2}
        alignItems="flex-start"
        justifyContent="flex-start"
        width={drawerOpen2 ? SECONDARY_DRAWER_WIDTH : 0}
      >
        <Previewer
          onClose={() => {
            toggleDrawer2(false)
          }}
        />
      </MenuCont2>
      <RenderWrapper
        animate
        className="render-wrapper"
        pl={isTabletOrMobile ? 0 : drawerOpen ? PRIMARY_DRAWER_WIDTH : '64px'}
        pr={isTabletOrMobile ? 0 : drawerOpen2 ? SECONDARY_DRAWER_WIDTH : '0'}
      >
        {children}
        {isTabletOrMobile && (
          <Overlay
            onClick={toggleDrawer}
            initial={{ opacity: 0 }}
            pointerEvents={drawerOpen ? 'auto' : 'none'}
            animate={drawerOpen ? { opacity: 1 } : { opacity: 0 }}
          />
        )}
      </RenderWrapper>
    </>
  )
}

export default SideBar

SideBar.defaultProps = {
  color: 'white',
  bg: 'gray.900',
  hoverColor: 'gray.800',
  borderColor: 'gray.700',
  accentColor: 'cyan.500'
}
